import { showErrorToast, showSuccessToast } from "@/components/ui/toast";
import {
  createShifts,
  deleteShift,
  getShifts,
  publishShifts,
  updateShift,
} from "@/services/shift.service";
import type {
  ShiftCreateInput,
  ShiftUpdateInput,
} from "@/types/shift.type";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Lister les shifts d'une période
export const useFetchShifts = (
  params: {
    from: string;
    to: string;
    employeeId?: string;
    companyId?: string;
  },
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: ["shifts", params],
    queryFn: () => getShifts(params),
    enabled: options?.enabled ?? true,
  });
};

// Formate un shift skippé en message lisible pour un toast
const formatSkipped = (
  skipped: { reason: string; employee?: { firstName: string | null; lastName: string | null } | null }[]
) => {
  const lines = skipped.slice(0, 5).map((s) => {
    const name = s.employee
      ? `${s.employee.firstName ?? ""} ${s.employee.lastName ?? ""}`.trim()
      : "Employé inconnu";
    return `• ${name} — ${s.reason}`;
  });
  if (skipped.length > 5) {
    lines.push(`• ...et ${skipped.length - 5} autre(s)`);
  }
  return lines.join("\n");
};

// Créer un ou plusieurs shifts
export const useCreateShifts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      payload: ShiftCreateInput | { shifts: ShiftCreateInput[] }
    ) => createShifts(payload),
    onSuccess: (data) => {
      const { created, skipped, message } = data;

      if (created > 0 && skipped.length === 0) {
        // Tous créés
        showSuccessToast(message);
      } else if (created > 0 && skipped.length > 0) {
        // Succès partiel : certains créés, d'autres en conflit
        showSuccessToast(`${message}\n${formatSkipped(skipped)}`);
      } else {
        // Aucun créé (tous en conflit)
        showErrorToast(`${message}\n${formatSkipped(skipped)}`);
      }

      queryClient.invalidateQueries({ queryKey: ["shifts"] });
    },
    onError: (error) => {
      showErrorToast(
        error instanceof Error ? error.message : "Une erreur est survenue"
      );
    },
  });
};

// Modifier un shift
export const useUpdateShift = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ShiftUpdateInput }) =>
      updateShift(id, data),
    onSuccess: () => {
      showSuccessToast("Créneau modifié avec succès");
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
    },
    onError: (error) => {
      showErrorToast(
        error instanceof Error ? error.message : "Une erreur est survenue"
      );
    },
  });
};

// Publier les shifts d'une semaine
export const usePublishShifts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { from: string; to: string; companyId?: string }) =>
      publishShifts(params),
    onSuccess: (data) => {
      showSuccessToast(data.message);
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
    },
    onError: (error) => {
      showErrorToast(
        error instanceof Error ? error.message : "Une erreur est survenue"
      );
    },
  });
};

// Supprimer un shift
export const useDeleteShift = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteShift,
    onSuccess: () => {
      showSuccessToast("Créneau supprimé avec succès");
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
    },
    onError: (error) => {
      showErrorToast(
        error instanceof Error ? error.message : "Une erreur est survenue"
      );
    },
  });
};
